import { Add, CheckCircle, ContentCopy, DeleteOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  ListItemText,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { ChangeEvent, ReactNode } from "react";
import { useTranslate } from "react-admin";

import { GenderEnum, LocationTypeEnum, PractitionerStatus, Salutation } from "../../generated/graphql";
import { borderRadius, color, space, typography } from "../../theme/designTokens";

let nextDraftId = 0;

const createDraftId = () => {
  nextDraftId += 1;
  return `manual-setup-${nextDraftId}`;
};

export type ManualLocationChoice = {
  id: string;
  name: string;
  partOfId?: string | null;
  type: LocationTypeEnum;
};

export type ManualWardDraft = {
  id: string;
  name: string;
};

export type ManualLocationDraft = {
  id: string;
  name: string;
  wards: ManualWardDraft[];
};

export type ManualPractitionerDraft = {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  salutation: Salutation | "";
  siteId: string;
  status: PractitionerStatus;
  wardIds: string[];
  workerNumber: string;
};

export type ManualPatientDraft = {
  birthDate: string;
  firstName: string;
  gender: GenderEnum | "";
  id: string;
  lastName: string;
  patientNumber: string;
  salutation: Salutation | "";
  siteId: string;
  wardId: string;
};

// A record that already exists in the portal, shown read-only above the drafts.
export type ExistingRecord = {
  id: string;
  primary: string;
  secondary?: string;
};

export type ExistingRecordsByResource = {
  location: ExistingRecord[];
  patient: ExistingRecord[];
  practitioner: ExistingRecord[];
};

export type ManualSetupSubmission = {
  locations: ManualLocationDraft[];
  patients: ManualPatientDraft[];
  practitioners: ManualPractitionerDraft[];
};

export const createManualWardDraft = (): ManualWardDraft => ({ id: createDraftId(), name: "" });

export const createManualLocationDraft = (): ManualLocationDraft => ({
  id: createDraftId(),
  name: "",
  wards: [createManualWardDraft()],
});

export const createManualPractitionerDraft = (): ManualPractitionerDraft => ({
  email: "",
  firstName: "",
  id: createDraftId(),
  lastName: "",
  salutation: "",
  siteId: "",
  status: PractitionerStatus.Active,
  wardIds: [],
  workerNumber: "",
});

export const createManualPatientDraft = (): ManualPatientDraft => ({
  birthDate: "",
  firstName: "",
  gender: "",
  id: createDraftId(),
  lastName: "",
  patientNumber: "",
  salutation: "",
  siteId: "",
  wardId: "",
});

export const getSiteChoices = (choices: ManualLocationChoice[]) =>
  choices.filter(choice => choice.type === LocationTypeEnum.Site);

export const getWardChoices = (choices: ManualLocationChoice[], siteId: string) =>
  siteId ? choices.filter(choice => choice.type === LocationTypeEnum.Ward && choice.partOfId === siteId) : [];

// A practitioner works in the selected wards, or in the whole site when it has none.
export const getPractitionerLocationIds = (draft: ManualPractitionerDraft) =>
  draft.wardIds.length > 0 ? draft.wardIds : [draft.siteId];

export const getPatientLocationId = (draft: ManualPatientDraft) => draft.wardId || draft.siteId;

const hasValue = (value: string) => value.trim().length > 0;
const isEmailValid = (value: string) => !hasValue(value) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

// Untouched rows (for example the initial empty row) are ignored for counts,
// validation, and submission so an unused row never blocks the journey.
export const isManualWardDraftFilled = (ward: ManualWardDraft) => hasValue(ward.name);

export const isManualLocationDraftFilled = (draft: ManualLocationDraft) =>
  hasValue(draft.name) || draft.wards.some(isManualWardDraftFilled);

export const isManualPractitionerDraftFilled = (draft: ManualPractitionerDraft) =>
  [draft.firstName, draft.lastName, draft.workerNumber, draft.email, draft.siteId, draft.salutation].some(hasValue) ||
  draft.wardIds.length > 0;

export const isManualPatientDraftFilled = (draft: ManualPatientDraft) =>
  [
    draft.firstName,
    draft.lastName,
    draft.birthDate,
    draft.patientNumber,
    draft.siteId,
    draft.wardId,
    draft.salutation,
    draft.gender,
  ].some(hasValue);

export type ManualLocationTotals = {
  sites: number;
  wards: number;
};

export const getManualLocationTotals = (drafts: ManualLocationDraft[]): ManualLocationTotals =>
  drafts.filter(isManualLocationDraftFilled).reduce<ManualLocationTotals>(
    (totals, draft) => ({
      sites: totals.sites + 1,
      wards: totals.wards + draft.wards.filter(isManualWardDraftFilled).length,
    }),
    { sites: 0, wards: 0 }
  );

export const isManualLocationDraftValid = (draft: ManualLocationDraft) => hasValue(draft.name);

// A ward is mandatory as soon as the chosen site has wards, like in the design.
const isWardAssignmentValid = (choices: ManualLocationChoice[], siteId: string, wardIds: string[]) => {
  if (!getSiteChoices(choices).some(site => site.id === siteId)) {
    return false;
  }
  const wards = getWardChoices(choices, siteId);
  if (wards.length === 0) {
    return wardIds.length === 0;
  }
  return wardIds.length > 0 && wardIds.every(wardId => wards.some(ward => ward.id === wardId));
};

export const isManualPractitionerDraftValid = (draft: ManualPractitionerDraft, choices: ManualLocationChoice[]) =>
  hasValue(draft.firstName) &&
  hasValue(draft.lastName) &&
  hasValue(draft.workerNumber) &&
  hasValue(draft.salutation) &&
  isEmailValid(draft.email) &&
  isWardAssignmentValid(choices, draft.siteId, draft.wardIds);

export const isManualPatientDraftValid = (draft: ManualPatientDraft, choices: ManualLocationChoice[]) =>
  hasValue(draft.firstName) &&
  hasValue(draft.lastName) &&
  hasValue(draft.birthDate) &&
  hasValue(draft.patientNumber) &&
  hasValue(draft.salutation) &&
  hasValue(draft.gender) &&
  isWardAssignmentValid(choices, draft.siteId, draft.wardId ? [draft.wardId] : []);

type EditorPanelHeaderProps = {
  countLabel: string;
  description: string;
  title: string;
};

const EditorPanelHeader = ({ countLabel, description, title }: EditorPanelHeaderProps) => (
  <Box sx={{ alignItems: "flex-start", display: "flex", gap: space.xs }}>
    <Box sx={{ display: "flex", flex: 1, flexDirection: "column", gap: space.xxs, minWidth: 0 }}>
      <Typography fontWeight={typography.fontWeight.semibold} variant="subtitle1">
        {title}
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {description}
      </Typography>
    </Box>
    <Typography
      sx={{
        backgroundColor: color.datagridHeaderBackground,
        borderRadius: borderRadius.lg,
        color: "text.secondary",
        flexShrink: 0,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.semibold,
        px: 1.5,
        py: 0.75,
      }}
    >
      {countLabel}
    </Typography>
  </Box>
);

type ExistingRecordsListProps = {
  records: ExistingRecord[];
  testId: string;
};

const ExistingRecordsList = ({ records, testId }: ExistingRecordsListProps) => {
  const translate = useTranslate();
  if (records.length === 0) {
    return null;
  }
  return (
    <Box
      data-testid={testId}
      sx={{
        backgroundColor: color.background2,
        border: `1px solid ${color.panelBorder}`,
        borderRadius: borderRadius.md + 4,
        display: "flex",
        flexDirection: "column",
        gap: space.xxs,
        p: 1.5,
      }}
    >
      <Box sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: space.xxs }}>
        <CheckCircle fontSize="small" sx={{ color: color.plannedText }} />
        <Typography fontWeight={typography.fontWeight.semibold} variant="body2">
          {translate("onboarding.manual.existing.title", { smart_count: records.length })}
        </Typography>
        <Typography color="text.secondary" variant="body2">
          {translate("onboarding.manual.existing.description")}
        </Typography>
      </Box>
      <Box
        component="ul"
        sx={{ display: "flex", flexWrap: "wrap", gap: space.xxs, m: 0, maxHeight: 132, overflowY: "auto", p: 0 }}
      >
        {records.map(record => (
          <Box
            component="li"
            key={record.id}
            sx={{
              alignItems: "baseline",
              backgroundColor: color.white,
              border: `1px solid ${color.panelBorder}`,
              borderRadius: borderRadius.md,
              display: "flex",
              gap: 0.75,
              listStyle: "none",
              px: 1.25,
              py: 0.5,
            }}
          >
            <Typography fontWeight={typography.fontWeight.medium} variant="body2">
              {record.primary}
            </Typography>
            {record.secondary && (
              <Typography color="text.secondary" variant="caption">
                {record.secondary}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const editorPanelSx = {
  backgroundColor: color.white,
  border: `1px solid ${color.panelBorder}`,
  borderRadius: borderRadius.md * 2,
  display: "flex",
  flexDirection: "column",
  gap: space.xs,
  p: 2.5,
} as const;

const compactFieldSx = {
  "& .MuiInputBase-input": {
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.medium,
    py: 1.25,
  },
  "& .MuiOutlinedInput-root": {
    borderRadius: borderRadius.md + 2,
    height: 42,
  },
} as const;

type RowActionsProps = {
  disabled: boolean;
  duplicateLabel: string;
  onDuplicate: () => void;
  onRemove: () => void;
  removeLabel: string;
};

const RowActions = ({ disabled, duplicateLabel, onDuplicate, onRemove, removeLabel }: RowActionsProps) => (
  <Box sx={{ alignItems: "center", display: "flex", flexShrink: 0, gap: space.xxs }}>
    <IconButton
      aria-label={duplicateLabel}
      disabled={disabled}
      onClick={onDuplicate}
      size="small"
      sx={{ border: `1px solid ${color.panelBorder}`, borderRadius: borderRadius.md + 1, height: 36, width: 36 }}
    >
      <ContentCopy fontSize="small" />
    </IconButton>
    <IconButton
      aria-label={removeLabel}
      disabled={disabled}
      onClick={onRemove}
      size="small"
      sx={{ border: `1px solid ${color.panelBorder}`, borderRadius: borderRadius.md + 1, height: 36, width: 36 }}
    >
      <DeleteOutline fontSize="small" />
    </IconButton>
  </Box>
);

type ManualLocationsEditorProps = {
  disabled: boolean;
  drafts: ManualLocationDraft[];
  existingRecords: ExistingRecord[];
  onChange: (drafts: ManualLocationDraft[]) => void;
};

export const ManualLocationsEditor = ({ disabled, drafts, existingRecords, onChange }: ManualLocationsEditorProps) => {
  const translate = useTranslate();

  const updateLocation = (id: string, update: (draft: ManualLocationDraft) => ManualLocationDraft) => {
    onChange(drafts.map(draft => (draft.id === id ? update(draft) : draft)));
  };

  const duplicateLocation = (draft: ManualLocationDraft) => {
    onChange([
      ...drafts,
      {
        id: createDraftId(),
        name: translate("onboarding.manual.copyName", { name: draft.name }),
        wards: draft.wards.map(ward => ({ id: createDraftId(), name: ward.name })),
      },
    ]);
  };

  return (
    <Box data-testid="manual-location-editor" sx={editorPanelSx}>
      <EditorPanelHeader
        countLabel={translate("onboarding.manual.location.count", {
          smart_count: getManualLocationTotals(drafts).sites,
        })}
        description={translate("onboarding.manual.location.description")}
        title={translate("onboarding.manual.location.title")}
      />
      <ExistingRecordsList records={existingRecords} testId="manual-existing-location" />
      <Stack spacing={space.xs}>
        {drafts.map((draft, locationIndex) => (
          <Box
            data-testid="manual-location-row"
            key={draft.id}
            sx={{
              border: `1px solid ${color.panelBorder}`,
              borderRadius: borderRadius.md + 4,
              display: "flex",
              flexDirection: "column",
              gap: 1.25,
              minWidth: 620,
              p: 1.75,
            }}
          >
            <Box sx={{ alignItems: "center", display: "grid", gap: 1.25, gridTemplateColumns: "32px 1fr 82px" }}>
              <Typography fontWeight={typography.fontWeight.semibold} textAlign="center" variant="body2">
                {locationIndex + 1}
              </Typography>
              <TextField
                disabled={disabled}
                fullWidth
                inputProps={{ "aria-label": translate("onboarding.manual.location.name") }}
                onChange={event => updateLocation(draft.id, current => ({ ...current, name: event.target.value }))}
                placeholder={translate("onboarding.manual.location.namePlaceholder")}
                required
                size="small"
                sx={compactFieldSx}
                value={draft.name}
              />
              <RowActions
                disabled={disabled}
                duplicateLabel={translate("onboarding.manual.location.duplicate", { number: locationIndex + 1 })}
                onDuplicate={() => duplicateLocation(draft)}
                onRemove={() => onChange(drafts.filter(current => current.id !== draft.id))}
                removeLabel={translate("onboarding.manual.location.remove", { number: locationIndex + 1 })}
              />
            </Box>

            <Box
              sx={{
                borderLeft: `2px solid ${color.panelBorder}`,
                display: "flex",
                flexDirection: "column",
                gap: space.xxs,
                ml: 1.5,
                pl: 2,
              }}
            >
              {draft.wards.map((ward, wardIndex) => (
                <Box
                  data-testid="manual-ward-row"
                  key={ward.id}
                  sx={{ alignItems: "center", display: "grid", gap: 1.25, gridTemplateColumns: "32px 1fr 82px" }}
                >
                  <Typography color="text.secondary" fontWeight={typography.fontWeight.medium} textAlign="center">
                    {wardIndex + 1}
                  </Typography>
                  <TextField
                    disabled={disabled}
                    fullWidth
                    inputProps={{
                      "aria-label": translate("onboarding.manual.location.wardName", { number: wardIndex + 1 }),
                    }}
                    onChange={event =>
                      updateLocation(draft.id, current => ({
                        ...current,
                        wards: current.wards.map(currentWard =>
                          currentWard.id === ward.id ? { ...currentWard, name: event.target.value } : currentWard
                        ),
                      }))
                    }
                    placeholder={translate("onboarding.manual.location.wardPlaceholder")}
                    required
                    size="small"
                    sx={compactFieldSx}
                    value={ward.name}
                  />
                  <RowActions
                    disabled={disabled}
                    duplicateLabel={translate("onboarding.manual.location.duplicateWard", {
                      number: wardIndex + 1,
                    })}
                    onDuplicate={() =>
                      updateLocation(draft.id, current => ({
                        ...current,
                        wards: [...current.wards, { id: createDraftId(), name: ward.name }],
                      }))
                    }
                    onRemove={() =>
                      updateLocation(draft.id, current => ({
                        ...current,
                        wards: current.wards.filter(currentWard => currentWard.id !== ward.id),
                      }))
                    }
                    removeLabel={translate("onboarding.manual.location.removeWard", { number: wardIndex + 1 })}
                  />
                </Box>
              ))}
              <Button
                disabled={disabled}
                onClick={() =>
                  updateLocation(draft.id, current => ({
                    ...current,
                    wards: [...current.wards, createManualWardDraft()],
                  }))
                }
                size="small"
                startIcon={<Add />}
                sx={{ alignSelf: "flex-start", px: 0 }}
                variant="text"
              >
                {translate("onboarding.manual.location.addWard")}
              </Button>
            </Box>
          </Box>
        ))}
      </Stack>
      <Button
        disabled={disabled}
        onClick={() => onChange([...drafts, createManualLocationDraft()])}
        startIcon={<Add />}
        sx={{ alignSelf: "flex-start", px: 0 }}
        variant="text"
      >
        {translate("onboarding.manual.location.add")}
      </Button>
    </Box>
  );
};

type NativeSelectProps = {
  ariaLabel: string;
  children: ReactNode;
  disabled: boolean;
  onChange: (value: string) => void;
  required?: boolean;
  value: string;
};

const NativeSelect = ({ ariaLabel, children, disabled, onChange, required, value }: NativeSelectProps) => (
  <TextField
    disabled={disabled}
    fullWidth
    inputProps={{ "aria-label": ariaLabel }}
    onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
    required={required}
    select
    SelectProps={{ native: true }}
    size="small"
    sx={compactFieldSx}
    value={value}
  >
    {children}
  </TextField>
);

type SiteSelectProps = {
  disabled: boolean;
  onChange: (siteId: string) => void;
  sites: ManualLocationChoice[];
  value: string;
};

const SiteSelect = ({ disabled, onChange, sites, value }: SiteSelectProps) => {
  const translate = useTranslate();
  return (
    <NativeSelect
      ariaLabel={translate("onboarding.manual.site")}
      disabled={disabled}
      onChange={onChange}
      required
      value={value}
    >
      <option value="">{translate("onboarding.manual.sitePlaceholder")}</option>
      {sites.map(site => (
        <option key={site.id} value={site.id}>
          {site.name}
        </option>
      ))}
    </NativeSelect>
  );
};

const getWardPlaceholderKey = (siteId: string, wards: ManualLocationChoice[]) =>
  !siteId
    ? "onboarding.manual.wardPlaceholder.selectSite"
    : wards.length === 0
      ? "onboarding.manual.wardPlaceholder.none"
      : null;

type WardSelectProps = {
  disabled: boolean;
  onChange: (wardId: string) => void;
  siteId: string;
  value: string;
  wards: ManualLocationChoice[];
};

const WardSelect = ({ disabled, onChange, siteId, value, wards }: WardSelectProps) => {
  const translate = useTranslate();
  const placeholderKey = getWardPlaceholderKey(siteId, wards);
  return (
    <NativeSelect
      ariaLabel={translate("onboarding.manual.ward")}
      disabled={disabled || placeholderKey !== null}
      onChange={onChange}
      required
      value={value}
    >
      <option value="">{translate(placeholderKey ?? "onboarding.manual.wardPlaceholder.selectOne")}</option>
      {wards.map(ward => (
        <option key={ward.id} value={ward.id}>
          {ward.name}
        </option>
      ))}
    </NativeSelect>
  );
};

const MAX_VISIBLE_WARD_CHIPS = 2;

const wardChipSx = {
  backgroundColor: color.selectedOptionBackground,
  borderRadius: borderRadius.md,
  color: color.primary,
  fontSize: typography.fontSize.xxs,
  fontWeight: typography.fontWeight.semibold,
  height: 24,
  maxWidth: 120,
} as const;

type WardMultiSelectProps = {
  disabled: boolean;
  onChange: (wardIds: string[]) => void;
  siteId: string;
  value: string[];
  wards: ManualLocationChoice[];
};

const WardMultiSelect = ({ disabled, onChange, siteId, value, wards }: WardMultiSelectProps) => {
  const translate = useTranslate();
  const placeholderKey = getWardPlaceholderKey(siteId, wards);

  const renderValue = (selected: string[]) => {
    if (selected.length === 0) {
      return (
        <Typography color="text.disabled" noWrap variant="body2">
          {translate(placeholderKey ?? "onboarding.manual.wardPlaceholder.selectMany")}
        </Typography>
      );
    }
    const names = selected.map(wardId => wards.find(ward => ward.id === wardId)?.name ?? "");
    const hiddenCount = names.length - MAX_VISIBLE_WARD_CHIPS;
    return (
      <Box sx={{ alignItems: "center", display: "flex", gap: 0.5, overflow: "hidden" }}>
        {names.slice(0, MAX_VISIBLE_WARD_CHIPS).map(name => (
          <Chip key={name} label={name} size="small" sx={wardChipSx} />
        ))}
        {hiddenCount > 0 && (
          <Chip label={translate("onboarding.manual.moreWards", { count: hiddenCount })} size="small" sx={wardChipSx} />
        )}
      </Box>
    );
  };

  return (
    <Select<string[]>
      disabled={disabled || placeholderKey !== null}
      displayEmpty
      fullWidth
      inputProps={{ "aria-label": translate("onboarding.manual.wards") }}
      multiple
      onChange={(event: SelectChangeEvent<string[]>) => {
        const next = event.target.value;
        onChange(typeof next === "string" ? next.split(",") : next);
      }}
      renderValue={renderValue}
      required
      size="small"
      sx={{
        borderRadius: borderRadius.md + 2,
        fontSize: typography.fontSize.xs,
        fontWeight: typography.fontWeight.medium,
        height: 42,
        "& .MuiSelect-select": { display: "flex", py: 1 },
      }}
      value={value}
    >
      {wards.map(ward => (
        <MenuItem dense key={ward.id} value={ward.id}>
          <Checkbox checked={value.includes(ward.id)} size="small" sx={{ mr: 1, p: 0 }} tabIndex={-1} />
          <ListItemText primary={ward.name} />
        </MenuItem>
      ))}
    </Select>
  );
};

type PersonEditorTableProps = {
  addLabel: string;
  children: ReactNode;
  countLabel: string;
  description: string;
  disabled: boolean;
  existingRecords: ExistingRecord[];
  headers: Array<{ label: string; required?: boolean; width: number }>;
  minWidth: number;
  onAdd: () => void;
  testId: string;
  title: string;
};

const PersonEditorTable = ({
  addLabel,
  children,
  countLabel,
  description,
  disabled,
  existingRecords,
  headers,
  minWidth,
  onAdd,
  testId,
  title,
}: PersonEditorTableProps) => (
  <Box data-testid={testId} sx={editorPanelSx}>
    <EditorPanelHeader countLabel={countLabel} description={description} title={title} />
    <ExistingRecordsList records={existingRecords} testId={`${testId}-existing`} />
    <TableContainer
      sx={{ border: `1px solid ${color.panelBorder}`, borderRadius: borderRadius.md + 4, boxShadow: "none" }}
    >
      <Table
        size="small"
        sx={{
          minWidth,
          tableLayout: "fixed",
          "& .MuiTableCell-head": {
            backgroundColor: color.background2,
            color: "text.secondary",
            fontSize: typography.fontSize.xs,
            fontWeight: typography.fontWeight.semibold,
            py: 1.5,
            whiteSpace: "nowrap",
          },
          "& .MuiTableCell-root": {
            borderColor: color.panelBorder,
            boxSizing: "border-box",
            overflow: "hidden",
            px: 1,
          },
          "& .MuiTableCell-body": {
            py: 1.25,
          },
        }}
      >
        <TableHead>
          <TableRow>
            {headers.map((header, index) => (
              <TableCell
                align={index === 0 || index === headers.length - 1 ? "center" : "left"}
                key={header.label}
                sx={{ width: header.width }}
              >
                {header.label}
                {header.required && (
                  <Box component="span" sx={{ color: color.error }}>
                    {" *"}
                  </Box>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{children}</TableBody>
      </Table>
    </TableContainer>
    <Button
      disabled={disabled}
      onClick={onAdd}
      startIcon={<Add />}
      sx={{ alignSelf: "flex-start", px: 0 }}
      variant="text"
    >
      {addLabel}
    </Button>
  </Box>
);

const salutationOptions = [Salutation.Mister, Salutation.Missus, Salutation.Others];

type ManualPractitionersEditorProps = {
  disabled: boolean;
  drafts: ManualPractitionerDraft[];
  existingRecords: ExistingRecord[];
  locationChoices: ManualLocationChoice[];
  onChange: (drafts: ManualPractitionerDraft[]) => void;
};

export const ManualPractitionersEditor = ({
  disabled,
  drafts,
  existingRecords,
  locationChoices,
  onChange,
}: ManualPractitionersEditorProps) => {
  const translate = useTranslate();
  const sites = getSiteChoices(locationChoices);
  const updateDraft = (id: string, update: Partial<ManualPractitionerDraft>) =>
    onChange(drafts.map(draft => (draft.id === id ? { ...draft, ...update } : draft)));

  return (
    <PersonEditorTable
      addLabel={translate("onboarding.manual.practitioner.add")}
      countLabel={translate("onboarding.manual.practitioner.count", {
        smart_count: drafts.filter(isManualPractitionerDraftFilled).length,
      })}
      description={translate("onboarding.manual.practitioner.description")}
      disabled={disabled}
      existingRecords={existingRecords}
      headers={[
        { label: "#", width: 40 },
        { label: translate("salutationLabel"), required: true, width: 100 },
        { label: translate("firstName"), required: true, width: 105 },
        { label: translate("lastName"), required: true, width: 105 },
        { label: translate("onboarding.manual.practitioner.workerNumber"), required: true, width: 120 },
        { label: translate("onboarding.manual.practitioner.email"), width: 140 },
        { label: translate("onboarding.manual.site"), required: true, width: 150 },
        { label: translate("onboarding.manual.wards"), required: true, width: 210 },
        { label: translate("status"), width: 95 },
        { label: translate("onboarding.manual.actions"), width: 96 },
      ]}
      minWidth={1161}
      onAdd={() => onChange([...drafts, createManualPractitionerDraft()])}
      testId="manual-practitioner-editor"
      title={translate("onboarding.manual.practitioner.title")}
    >
      {drafts.map((draft, index) => (
        <TableRow data-testid="manual-practitioner-row" key={draft.id}>
          <TableCell align="center">
            <Typography fontWeight={typography.fontWeight.semibold} variant="body2">
              {index + 1}
            </Typography>
          </TableCell>
          <TableCell>
            <NativeSelect
              ariaLabel={translate("salutationLabel")}
              disabled={disabled}
              onChange={salutation => updateDraft(draft.id, { salutation: salutation as Salutation | "" })}
              required
              value={draft.salutation}
            >
              <option value="" />
              {salutationOptions.map(salutation => (
                <option key={salutation} value={salutation}>
                  {translate(`salutation.${salutation}`)}
                </option>
              ))}
            </NativeSelect>
          </TableCell>
          <TableCell>
            <TextField
              disabled={disabled}
              fullWidth
              inputProps={{ "aria-label": translate("firstName") }}
              onChange={event => updateDraft(draft.id, { firstName: event.target.value })}
              required
              size="small"
              sx={compactFieldSx}
              value={draft.firstName}
            />
          </TableCell>
          <TableCell>
            <TextField
              disabled={disabled}
              fullWidth
              inputProps={{ "aria-label": translate("lastName") }}
              onChange={event => updateDraft(draft.id, { lastName: event.target.value })}
              required
              size="small"
              sx={compactFieldSx}
              value={draft.lastName}
            />
          </TableCell>
          <TableCell>
            <TextField
              disabled={disabled}
              fullWidth
              inputProps={{ "aria-label": translate("workerNumber") }}
              onChange={event => updateDraft(draft.id, { workerNumber: event.target.value })}
              required
              size="small"
              sx={compactFieldSx}
              value={draft.workerNumber}
            />
          </TableCell>
          <TableCell>
            <TextField
              disabled={disabled}
              error={!isEmailValid(draft.email)}
              fullWidth
              inputProps={{ "aria-label": translate("email") }}
              onChange={event => updateDraft(draft.id, { email: event.target.value })}
              size="small"
              sx={compactFieldSx}
              type="email"
              value={draft.email}
            />
          </TableCell>
          <TableCell>
            <SiteSelect
              disabled={disabled}
              onChange={siteId => updateDraft(draft.id, { siteId, wardIds: [] })}
              sites={sites}
              value={draft.siteId}
            />
          </TableCell>
          <TableCell>
            <WardMultiSelect
              disabled={disabled}
              onChange={wardIds => updateDraft(draft.id, { wardIds })}
              siteId={draft.siteId}
              value={draft.wardIds}
              wards={getWardChoices(locationChoices, draft.siteId)}
            />
          </TableCell>
          <TableCell>
            <NativeSelect
              ariaLabel={translate("status")}
              disabled={disabled}
              onChange={status => updateDraft(draft.id, { status: status as PractitionerStatus })}
              value={draft.status}
            >
              {[PractitionerStatus.Active, PractitionerStatus.Inactive].map(status => (
                <option key={status} value={status}>
                  {translate(`practitionerStatus.${status}`)}
                </option>
              ))}
            </NativeSelect>
          </TableCell>
          <TableCell>
            <RowActions
              disabled={disabled}
              duplicateLabel={translate("onboarding.manual.practitioner.duplicate", { number: index + 1 })}
              onDuplicate={() => onChange([...drafts, { ...draft, id: createDraftId(), wardIds: [...draft.wardIds] }])}
              onRemove={() => onChange(drafts.filter(current => current.id !== draft.id))}
              removeLabel={translate("onboarding.manual.practitioner.remove", { number: index + 1 })}
            />
          </TableCell>
        </TableRow>
      ))}
    </PersonEditorTable>
  );
};

type ManualPatientsEditorProps = {
  disabled: boolean;
  drafts: ManualPatientDraft[];
  existingRecords: ExistingRecord[];
  locationChoices: ManualLocationChoice[];
  onChange: (drafts: ManualPatientDraft[]) => void;
};

export const ManualPatientsEditor = ({
  disabled,
  drafts,
  existingRecords,
  locationChoices,
  onChange,
}: ManualPatientsEditorProps) => {
  const translate = useTranslate();
  const sites = getSiteChoices(locationChoices);
  const updateDraft = (id: string, update: Partial<ManualPatientDraft>) =>
    onChange(drafts.map(draft => (draft.id === id ? { ...draft, ...update } : draft)));

  return (
    <PersonEditorTable
      addLabel={translate("onboarding.manual.patient.add")}
      countLabel={translate("onboarding.manual.patient.count", {
        smart_count: drafts.filter(isManualPatientDraftFilled).length,
      })}
      description={translate("onboarding.manual.patient.description")}
      disabled={disabled}
      existingRecords={existingRecords}
      headers={[
        { label: "#", width: 40 },
        { label: translate("salutationLabel"), required: true, width: 100 },
        { label: translate("firstName"), required: true, width: 105 },
        { label: translate("lastName"), required: true, width: 105 },
        { label: translate("birthDate"), required: true, width: 145 },
        { label: translate("genderLabel"), required: true, width: 105 },
        { label: translate("patientId"), required: true, width: 120 },
        { label: translate("onboarding.manual.site"), required: true, width: 150 },
        { label: translate("onboarding.manual.ward"), required: true, width: 170 },
        { label: translate("onboarding.manual.actions"), width: 96 },
      ]}
      minWidth={1136}
      onAdd={() => onChange([...drafts, createManualPatientDraft()])}
      testId="manual-patient-editor"
      title={translate("onboarding.manual.patient.title")}
    >
      {drafts.map((draft, index) => (
        <TableRow data-testid="manual-patient-row" key={draft.id}>
          <TableCell align="center">
            <Typography fontWeight={typography.fontWeight.semibold} variant="body2">
              {index + 1}
            </Typography>
          </TableCell>
          <TableCell>
            <NativeSelect
              ariaLabel={translate("salutationLabel")}
              disabled={disabled}
              onChange={salutation => updateDraft(draft.id, { salutation: salutation as Salutation | "" })}
              required
              value={draft.salutation}
            >
              <option value="" />
              {salutationOptions.map(salutation => (
                <option key={salutation} value={salutation}>
                  {translate(`salutation.${salutation}`)}
                </option>
              ))}
            </NativeSelect>
          </TableCell>
          <TableCell>
            <TextField
              disabled={disabled}
              fullWidth
              inputProps={{ "aria-label": translate("firstName") }}
              onChange={event => updateDraft(draft.id, { firstName: event.target.value })}
              required
              size="small"
              sx={compactFieldSx}
              value={draft.firstName}
            />
          </TableCell>
          <TableCell>
            <TextField
              disabled={disabled}
              fullWidth
              inputProps={{ "aria-label": translate("lastName") }}
              onChange={event => updateDraft(draft.id, { lastName: event.target.value })}
              required
              size="small"
              sx={compactFieldSx}
              value={draft.lastName}
            />
          </TableCell>
          <TableCell>
            <TextField
              disabled={disabled}
              fullWidth
              inputProps={{ "aria-label": translate("birthDate") }}
              onChange={event => updateDraft(draft.id, { birthDate: event.target.value })}
              required
              size="small"
              sx={compactFieldSx}
              type="date"
              value={draft.birthDate}
            />
          </TableCell>
          <TableCell>
            <NativeSelect
              ariaLabel={translate("genderLabel")}
              disabled={disabled}
              onChange={gender => updateDraft(draft.id, { gender: gender as GenderEnum | "" })}
              required
              value={draft.gender}
            >
              <option value="" />
              {[GenderEnum.Male, GenderEnum.Female, GenderEnum.Other].map(gender => (
                <option key={gender} value={gender}>
                  {translate(`gender.${gender}`)}
                </option>
              ))}
            </NativeSelect>
          </TableCell>
          <TableCell>
            <TextField
              disabled={disabled}
              fullWidth
              inputProps={{ "aria-label": translate("patientId") }}
              onChange={event => updateDraft(draft.id, { patientNumber: event.target.value })}
              required
              size="small"
              sx={compactFieldSx}
              value={draft.patientNumber}
            />
          </TableCell>
          <TableCell>
            <SiteSelect
              disabled={disabled}
              onChange={siteId => updateDraft(draft.id, { siteId, wardId: "" })}
              sites={sites}
              value={draft.siteId}
            />
          </TableCell>
          <TableCell>
            <WardSelect
              disabled={disabled}
              onChange={wardId => updateDraft(draft.id, { wardId })}
              siteId={draft.siteId}
              value={draft.wardId}
              wards={getWardChoices(locationChoices, draft.siteId)}
            />
          </TableCell>
          <TableCell>
            <RowActions
              disabled={disabled}
              duplicateLabel={translate("onboarding.manual.patient.duplicate", { number: index + 1 })}
              onDuplicate={() => onChange([...drafts, { ...draft, id: createDraftId() }])}
              onRemove={() => onChange(drafts.filter(current => current.id !== draft.id))}
              removeLabel={translate("onboarding.manual.patient.remove", { number: index + 1 })}
            />
          </TableCell>
        </TableRow>
      ))}
    </PersonEditorTable>
  );
};
